import React from 'react';
import { Card, Typography, Alert, Radio, Spin, Tag, Space, Tooltip } from 'antd';
import {
  WarningOutlined,
  CheckCircleOutlined,
  ExperimentOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import {
  useGetSifeiEnvironmentQuery,
  useUpdateSifeiEnvironmentMutation,
} from '../../services/facturacionApi';
import { useAppSelector } from '../../hooks/redux';
import { isSupportUser } from '../../utils/roleHelpers';

const { Title, Text, Paragraph } = Typography;

const SifeiEnvironmentTab: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const isSupport = isSupportUser(user?.email);

  const { data: environment, isLoading, error } = useGetSifeiEnvironmentQuery();
  const [updateEnvironment, { isLoading: isUpdating }] = useUpdateSifeiEnvironmentMutation();

  const handleEnvironmentChange = async (newEnvironment: 'qa' | 'prod') => {
    if (newEnvironment === environment?.environment) return;

    try {
      await updateEnvironment({ environment: newEnvironment }).unwrap();
    } catch (err) {
      console.error('Error updating SIFEI environment:', err);
    }
  };

  if (!isSupport) {
    return (
      <Alert
        message="Acceso Restringido"
        description="Esta sección solo está disponible para usuarios de soporte técnico."
        type="warning"
        showIcon
      />
    );
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Cargando configuración...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error al cargar"
        description="No se pudo obtener la configuración del ambiente SIFEI. Asegúrese de que la facturación esté activada."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      <Alert
        message="Configuración de Soporte"
        description="Esta configuración solo debe ser modificada por personal autorizado de soporte técnico."
        type="warning"
        showIcon
        icon={<WarningOutlined />}
        style={{ marginBottom: 24 }}
      />

      <Card>
        <Title level={4}>
          <RocketOutlined /> Ambiente SIFEI (PAC)
        </Title>

        <Paragraph type="secondary">
          Seleccione el ambiente de timbrado CFDI. El ambiente de QA es para pruebas,
          mientras que Producción genera facturas reales ante el SAT.
        </Paragraph>

        <div style={{ marginTop: 24 }}>
          <Text strong>Ambiente actual: </Text>
          {environment?.isProduction ? (
            <Tag color="red" icon={<RocketOutlined />}>PRODUCCIÓN</Tag>
          ) : (
            <Tag color="blue" icon={<ExperimentOutlined />}>QA (Pruebas)</Tag>
          )}
        </div>

        <div style={{ marginTop: 16 }}>
          <Text strong>URL del servicio: </Text>
          <Text code>{environment?.baseUrl}</Text>
        </div>

        <div style={{ marginTop: 32 }}>
          <Text strong style={{ display: 'block', marginBottom: 12 }}>
            Cambiar ambiente:
          </Text>

          <Radio.Group
            value={environment?.environment}
            onChange={(e) => handleEnvironmentChange(e.target.value)}
            disabled={isUpdating}
            buttonStyle="solid"
            size="large"
          >
            <Space direction="vertical" size="middle">
              <Tooltip title="Ambiente de pruebas - Las facturas no tienen validez fiscal">
                <Radio.Button value="qa" style={{ width: 300, height: 60, display: 'flex', alignItems: 'center' }}>
                  <Space>
                    <ExperimentOutlined style={{ fontSize: 24 }} />
                    <div>
                      <div style={{ fontWeight: 'bold' }}>QA (Pruebas)</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        devcfdi.sifei.com.mx
                      </div>
                    </div>
                  </Space>
                </Radio.Button>
              </Tooltip>

              <Tooltip title="Ambiente de producción - Las facturas son válidas ante el SAT">
                <Radio.Button
                  value="prod"
                  style={{
                    width: 300,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Space>
                    <RocketOutlined style={{ fontSize: 24, color: environment?.isProduction ? '#fff' : '#ff4d4f' }} />
                    <div>
                      <div style={{ fontWeight: 'bold' }}>PRODUCCIÓN</div>
                      <div style={{ fontSize: 12, color: environment?.isProduction ? 'rgba(255,255,255,0.8)' : 'var(--text-secondary)' }}>
                        micfdi.sifei.com.mx
                      </div>
                    </div>
                  </Space>
                </Radio.Button>
              </Tooltip>
            </Space>
          </Radio.Group>

          {isUpdating && (
            <div style={{ marginTop: 16 }}>
              <Spin size="small" /> Actualizando ambiente...
            </div>
          )}
        </div>

        {environment?.isProduction && (
          <Alert
            message="Ambiente de Producción Activo"
            description="Las facturas generadas serán válidas ante el SAT. Asegúrese de que el cliente esté listo para facturar en producción."
            type="error"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginTop: 24 }}
          />
        )}
      </Card>
    </div>
  );
};

export default SifeiEnvironmentTab;
